import { Core } from "@faire/design-tokens";
import { setTimeoutIfPossible } from "@faire/next-utils/isomorphic/setTimeoutIfPossible";
import { SlideIn } from "@faire/retailer-visitor-shared/components/SlideIn";
import { handleKeyDown } from "@faire/retailer-visitor-shared/lib/handleKeyDown";
import { Carousel } from "@faire/web--source/slate-labs/Carousel/Carousel";
import { Button } from "@faire/web--source/slate/Button";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { cn } from "@faire/web--source/tools/cn";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { trackLoggedOutRetailersLoggedOutHomeRedesignCtaClick } from "@faire/web-api--source/events/loggedOutRetailers/click/loggedOutHomeRedesignCta";
import { trackLoggedOutRetailersLoggedOutHomeRedesignView } from "@faire/web-api--source/events/loggedOutRetailers/view/loggedOutHomeRedesign";
import { Dispatch, SetStateAction } from "react";
import * as React from "react";

// eslint-disable-next-line import/no-unassigned-import
import "./pillWithContentAnimation.tw.css";

const DRAG_DISTANCE_THRESHOLD = 20;
export interface ITabProps {
  tabName: string | undefined;
  pathIdentifier: string | undefined;
  ctaLink: string | undefined;
  ctaLinkText?: string | undefined;
}

export interface IPillWithContentProps {
  title: string;
  tabs: Array<ITabProps> | undefined;
  eventTrackingName: string;
  children: React.ReactNode;
  currentIndex: number;
  pauseScroll: () => void;
  resumeScroll: () => void;
  isVisible: boolean;
  setCurrentIndex: Dispatch<SetStateAction<number>>;
  loadTimingRef?: React.RefCallback<HTMLElement>;
}

// use for USA homepage only
export const PillWithContent: React.FC<IPillWithContentProps> = ({
  tabs,
  title,
  children,
  eventTrackingName,
  loadTimingRef,
  pauseScroll,
  resumeScroll,
  currentIndex,
  setCurrentIndex,
  isVisible,
}) => {
  const { isXLargeAndAbove } = useViewport();

  const selectedTab = tabs?.[currentIndex];

  const handleClickTab = (index: number, tabName: string | undefined) => {
    if (tabName) {
      trackLoggedOutRetailersLoggedOutHomeRedesignCtaClick(
        `${eventTrackingName}-tab`,
        tabName,
        "v2"
      );
    }
    pauseScroll();
    setCurrentIndex(index);
    setTimeoutIfPossible(() => {
      resumeScroll();
    }, 12000);
  };

  const handleDrag = (
    e: React.DragEvent<HTMLDivElement>,
    index: number,
    tabName: string | undefined
  ) => {
    const dragDistance = Math.sqrt(
      Math.pow(e.movementX, 2) + Math.pow(e.movementY, 2)
    );
    if (dragDistance < DRAG_DISTANCE_THRESHOLD) {
      // treat as click
      handleClickTab(index, tabName);
    }
  };

  const headerRef = useDoActionWhenVisible(() =>
    trackLoggedOutRetailersLoggedOutHomeRedesignView(
      `${eventTrackingName}-header`,
      "v2"
    )
  );

  const tabRef = useDoActionWhenVisible(() =>
    trackLoggedOutRetailersLoggedOutHomeRedesignView(
      `${eventTrackingName}-tab`,
      "v2"
    )
  );

  return (
    <Column
      className="mt-12 mb-0 ml-4 md:ml-8 lg:ml-12 2xl:ml-20"
      ref={loadTimingRef}
    >
      <SlideIn bottomThreshold={100} offset={getSpacing("5x")}>
        <Column className="items-start">
          <Typography
            variant={{
              mobileAndAbove: "pageHeaderSerifRegular",
              xLargeAndAbove: "displaySSerifRegular",
              xxLargeDesktop: "displayMSerifRegular",
            }}
            ref={headerRef}
          >
            {title}
          </Typography>
          <Spacer
            height={{
              mobileAndAbove: "3x",
              tabletAndAbove: "2x",
              desktopAndAbove: "3x",
            }}
          />
          <Row justify="space-between" className="w-full">
            <div className="overflow-auto" ref={tabRef}>
              <Carousel
                items={tabs ?? []}
                renderItem={(tab, index) => (
                  <div
                    key={index}
                    onClick={() => handleClickTab(index, tab.pathIdentifier)}
                    onKeyDown={handleKeyDown(() =>
                      handleClickTab(index, tab.pathIdentifier)
                    )}
                    onDrag={(e) => handleDrag(e, index, tab.pathIdentifier)}
                  >
                    <Typography
                      tabIndex={0}
                      className={cn(
                        "hover:border-core-border-default focus-visible:border-core-border-default flex h-10 cursor-pointer items-center justify-center rounded-[40px] border p-4!",

                        index === currentIndex
                          ? "border-core-border-default bg-core-surface-default"
                          : "border-core-border-muted"
                      )}
                      color={
                        index === currentIndex ? Core.text.inverse : undefined
                      }
                    >
                      {tab.tabName}
                    </Typography>
                  </div>
                )}
                freeFlow
                showNavigationArrows={false}
                itemGap={8}
                loop={false}
                className="w-full"
                onVisible={() => {
                  trackLoggedOutRetailersLoggedOutHomeRedesignView(
                    `${eventTrackingName}-pill-onscroll`,
                    "v2"
                  );
                }}
                noMobilePadding
              />
            </div>

            {isXLargeAndAbove ? (
              <Button
                className="xl:pr-12! 2xl:pr-20!"
                variant="tertiary"
                linkTo={selectedTab?.ctaLink}
                onClick={() => {
                  trackLoggedOutRetailersLoggedOutHomeRedesignCtaClick(
                    "category_best_brands_banner_v2-cta",
                    selectedTab?.ctaLink ?? "",
                    "v2"
                  );
                }}
              >
                {selectedTab?.ctaLinkText}
              </Button>
            ) : null}
          </Row>
        </Column>

        <div
          className={cn(
            "relative w-full overflow-hidden pr-4 md:pr-8 lg:pr-12 2xl:pr-20",
            isVisible
              ? "pill-with-content-fade-in bottom-0 opacity-100"
              : "pill-with-content-fade-out bottom-10 opacity-0"
          )}
        >
          {children}
        </div>
      </SlideIn>
    </Column>
  );
};
